import cn from 'classnames'
import dynamic from 'next/dynamic'
import s from './Layout.module.scss'
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { useAuthUI } from '@corratech/pylot-auth-manager'
import { Navbar } from '@corratech/air-ooka-header'
import { LoadingIndicator } from '@components/LoadingIndicator'
import formStyles from '@commonStyles/Forms.module.scss'
import { useTranslation } from 'next-i18next'
import { AuthWrapper } from '@components/common/AuthWrapper'
import { useRouter } from 'next/router'
import { validate as EmailValidate } from 'email-validator'
import { useScrolledPosition } from '@corratech/air-ooka-header/hooks/useScrolledPosition'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'

const dynamicProps = {
    // eslint-disable-next-line react/display-name
    loading: () => <LoadingIndicator />
}
const LoginView = dynamic(
    () => import('@components/alfakher/pylot-auth/src/LoginView'),
    { ...dynamicProps }
)

const SignUpView = dynamic(
    () => import('@components/alfakher/pylot-auth/src/SignUpView'),
    { ...dynamicProps }
)
const ForgotPassword = dynamic(
    () => import('@components/alfakher/pylot-auth/src/ForgotPassword'),
    { ...dynamicProps }
)

const ResetPassword = dynamic(
    () => import('@components/alfakher/pylot-auth/src/ResetPassword'),
    { ...dynamicProps }
)

const Footer = dynamic(() => import('@components/common/Footer'), {
    ssr: false
})

const Sidebar = dynamic(
    () =>
        import('stores/ooka_uae_store/components/alfakher/pylot-ui/src/Sidebar')
)

const CartSidebarView = dynamic(
    () => import('@components/alfakher/pylot-cart/CartSidebarView')
)

const Modal = dynamic(
    () => import('stores/ooka_uae_store/components/alfakher/pylot-ui/src/Modal')
)

interface Props {
    authRequired: boolean
    pageProps: any
    setIsLoader?: Dispatch<SetStateAction<boolean>>
}

enum MODAL_VIEW_ENUMS {
    LOGIN_VIEW = 'LOGIN_VIEW',
    SIGNUP_VIEW = 'SIGNUP_VIEW',
    FORGOT_VIEW = 'FORGOT_VIEW',
    RESET_VIEW = 'PASSWORD_RESET_VIEW'
}

enum MODAL_HASH_ENUMS {
    LOGIN_VIEW = 'show-login-form',
    SIGNUP_VIEW = 'show-signup-form',
    FORGOT_VIEW = 'show-forgot-password',
    RESET_VIEW = 'show-reset-form',
    GUEST = 'session-expired'
}

const Layout: FC<Props> = ({ children, authRequired }) => {
    const { displaySidebar, closeSidebar, openToast } = useUI()
    const [scrolledClass] = useScrolledPosition()
    const [isMobileMenuOpened, setIsMobileMenuOpened] = useState(false)
    const { pageType, setPageType } = useAlfakherContext()
    const {
        modalView,
        displayModal,
        closeAuthModal,
        setModalView,
        openAuthModal
    } = useAuthUI()
    const [isAcceptedCookies, setIsAcceptedCookies] = useState<string | null>(
        null
    )

    const { t } = useTranslation('common')
    const [isHashChanged, setIsHashChanged] = useState<boolean>(false)

    const router = useRouter()

    useEffect(() => {
        const handleStorageChange = () => {
            setIsAcceptedCookies(localStorage.getItem('cookiefirst-id'))
        }
        window.addEventListener('storage', handleStorageChange)
        const originalSetItem = localStorage.setItem
        localStorage.setItem = function (...args: [string, string]) {
            originalSetItem.apply(this, args)
            if (args[0] === 'cookiefirst-id') handleStorageChange()
        }
        return () => {
            window.removeEventListener('storage', handleStorageChange)
            localStorage.setItem = originalSetItem
        }
    }, [])

    // used to disable transparent header when hit other pages
    useEffect(() => {
        if (
            !router.asPath.includes('/c/') ||
            !router.asPath.includes('/content/') ||
            !router.asPath.includes('/')
        ) {
            setPageType({
                transparentHeader: false,
                isNicotinePage: true
            })
        }
    }, [router.asPath])

    useEffect(() => {
        if (isAcceptedCookies !== 'null') {
            document.body.classList.add('cookie-accepted')
        } else {
            document.body.classList.remove('cookie-accepted')
        }
    }, [isAcceptedCookies])

    const onHashChangeComplete = (): void => {
        if (typeof window != 'undefined' && window.location.hash) {
            switch (window.location.hash.replace('#', '')) {
                case MODAL_HASH_ENUMS.LOGIN_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.LOGIN_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.SIGNUP_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.SIGNUP_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.FORGOT_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.FORGOT_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.RESET_VIEW:
                    {
                        const url = new URL(router.asPath, location.origin)
                        const email = url.searchParams.get('email')
                        const token = url.searchParams.get('token')

                        if (email && token && EmailValidate(email)) {
                            setModalView(MODAL_VIEW_ENUMS.RESET_VIEW, {
                                email: email,
                                token: token
                            })
                            setIsHashChanged(true)
                        }
                    }
                    break
                case MODAL_HASH_ENUMS.GUEST:
                    if (window?.localStorage.getItem('is_authorised') === '1') {
                        openToast(
                            t('Your session expired. Please sign in again.'),
                            ToastType.Warning
                        )
                        router.push('/')
                    }
                    break
            }
        }
    }

    const handleChatOpen = () => {
        if (typeof window !== 'undefined' && window.HubSpotConversations) {
            window.HubSpotConversations.widget.open()
        }
    }

    useEffect(() => {
        const openChatButton = document.querySelector('.openChat button')

        if (openChatButton) {
            openChatButton.addEventListener('click', handleChatOpen)
        }

        return () => {
            if (openChatButton) {
                openChatButton.removeEventListener('click', handleChatOpen)
            }
        }
    }, [router.asPath])

    useEffect(() => {
        onHashChangeComplete()
        window.addEventListener('hashchange', onHashChangeComplete, false)
        return () => {
            window.removeEventListener(
                'hashchange',
                onHashChangeComplete,
                false
            )
        }
    }, [])

    useEffect(() => {
        if (isHashChanged === true) {
            openAuthModal()
            setIsHashChanged(false)
        }
    }, [isHashChanged])

    /**
     * Remove hash when popup is closed
     */
    const closeModal = () => {
        closeAuthModal()
        if (window.location.hash) {
            history.replaceState(null, '', ' ')
        }
    }
    return (
        <div
            className={cn(
                s.root,
                `store_${router.locale}`,
                scrolledClass,
                pageType,
                isMobileMenuOpened && 'isMobileMenuOpened'
            )}
            id="layout-main"
        >
            <Navbar
                setIsMobileMenuOpened={setIsMobileMenuOpened}
                isMobileMenuOpened={isMobileMenuOpened}
            />

            <main className={cn(s['main-container'])} id="contentarea">
                <AuthWrapper authRequired={authRequired}>
                    {children}
                </AuthWrapper>
            </main>
            <Footer />
            {displayModal && (
                <Modal open={displayModal} onClose={closeModal}>
                    <div className={cn(formStyles.root)}>
                        {modalView === MODAL_VIEW_ENUMS.LOGIN_VIEW && (
                            <LoginView />
                        )}
                        {modalView === MODAL_VIEW_ENUMS.SIGNUP_VIEW && (
                            <SignUpView />
                        )}
                        {modalView === MODAL_VIEW_ENUMS.FORGOT_VIEW && (
                            <ForgotPassword />
                        )}

                        {modalView === MODAL_VIEW_ENUMS.RESET_VIEW && (
                            <ResetPassword />
                        )}
                    </div>
                </Modal>
            )}
            {displaySidebar && (
                <Sidebar open={displaySidebar} onClose={closeSidebar}>
                    <CartSidebarView />
                </Sidebar>
            )}
        </div>
    )
}

export default Layout
